import React, { useState } from 'react';

// Join an existing campaign via an invite code. Type the code, see a preview
// (campaign name, remaining uses), confirm to accept.

const normalizeCode = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const formatCode = (value) => normalizeCode(value).slice(0, 12).replace(/(.{4})(?=.)/g, '$1-');

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '10px 12px',
  fontSize: '1rem',
  fontFamily: 'monospace',
  letterSpacing: '0.08em',
  boxSizing: 'border-box',
});

const labelStyle = (theme) => ({
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.78rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.colors.textMuted,
});

export const JoinCampaignModal = ({ theme, onClose, onPreview, onAccept }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handlePreview = async () => {
    if (!onPreview || !code.trim()) return;
    setBusy(true); setError(null); setPreview(null);
    try {
      const result = await onPreview(code);
      setPreview(result);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async (e) => {
    e?.preventDefault();
    if (!onAccept) return;
    setBusy(true); setError(null);
    try {
      await onAccept(code);
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Join a campaign"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleAccept}
        style={{
          width: '100%', maxWidth: 460,
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
        }}
      >
        <h2 style={{
          margin: '0 0 4px',
          fontFamily: t.headingFamily,
          fontSize: '1.3rem',
          color: c.accent,
        }}>Join a Campaign</h2>
        <p style={{
          margin: '0 0 18px',
          color: c.textMuted,
          fontSize: '0.86rem',
          fontFamily: t.fontFamily,
        }}>Enter the 12-character invite code your DM shared with you.</p>

        <label style={labelStyle(theme)} htmlFor="jcm-code">Invite code</label>
        <input
          id="jcm-code"
          type="text"
          value={code}
          placeholder="ABCD-EFGH-JKLM"
          onChange={(e) => { setCode(formatCode(e.target.value)); setPreview(null); }}
          autoFocus
          style={fieldStyle(theme)}
        />

        {preview && (
          <div style={{
            marginTop: 12,
            padding: '10px 12px',
            background: preview.valid ? 'rgba(39,201,90,0.10)' : 'rgba(212,106,90,0.12)',
            border: `1px solid ${preview.valid ? c.success || '#27c95a' : c.danger || '#d46a5a'}`,
            borderRadius: 4,
            fontSize: '0.88rem',
            fontFamily: t.fontFamily,
          }}>
            {preview.valid
              ? <>Joining: <strong>{preview.campaignName}</strong>{preview.maxUses != null && <span style={{ color: c.textMuted }}> · {preview.useCount} / {preview.maxUses} used</span>}</>
              : <>Invite unavailable: {preview.reason}</>}
          </div>
        )}

        {error && (
          <div style={{
            marginTop: 14,
            padding: '8px 12px',
            background: 'rgba(212,106,90,0.15)',
            border: `1px solid ${c.danger || '#d46a5a'}`,
            color: c.danger || '#d46a5a',
            borderRadius: 4,
            fontSize: '0.86rem',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '9px 16px',
              background: 'transparent',
              color: c.textSecondary,
              border: `1px solid ${c.borderLight}`,
              borderRadius: 4,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontSize: '0.88rem',
            }}
          >Cancel</button>
          <button
            type="button"
            onClick={handlePreview}
            disabled={busy || !code.trim()}
            style={{
              padding: '9px 14px',
              background: 'transparent',
              color: c.textSecondary,
              border: `1px solid ${c.borderPrimary}`,
              borderRadius: 4,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontSize: '0.86rem',
            }}
          >Preview</button>
          <button
            type="submit"
            disabled={busy || !code.trim() || (preview && !preview.valid)}
            style={{
              padding: '9px 18px',
              background: c.accent,
              color: c.textOnAccent || '#000',
              border: 'none',
              borderRadius: 4,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontWeight: 600,
              fontSize: '0.9rem',
              opacity: (busy || !code.trim() || (preview && !preview.valid)) ? 0.7 : 1,
            }}
          >{busy ? 'Joining…' : 'Join'}</button>
        </div>
      </form>
    </div>
  );
};
